import { ApiError } from '@multaqa/api-client'
import type { PaymentTerm } from '@/features/accounting-tax/api'
import { localIsoDate } from '@/lib/company-date'
import { subtractDecimalStrings } from '@/lib/decimal'
import { formatErrorMessage } from '@/lib/error-message'
import { matchPaymentTerm } from '@/lib/payment-term-matching'
import type { Invoice } from './invoice-api-types'

export type InvoiceValidationErrors = {
  header?: Record<string, string>
  lines: Record<number, Record<string, string>>
}

export function formErrorText(
  error: Error,
  fallback: string,
  lineValidation: (line: number, message: string, field: string) => string,
): string {
  if (error instanceof ApiError && error.kind === 'validation' && error.errors) {
    const first = Object.entries(error.errors)[0]
    if (first) {
      const lineMatch = /^lines\.(\d+)\.(.+)$/.exec(first[0])
      const message = first[1].join(' ')
      if (lineMatch?.[1] && lineMatch[2]) {
        return lineValidation(Number(lineMatch[1]) + 1, message, lineMatch[2])
      }
      return message
    }
  }
  return formatErrorMessage(error, fallback)
}

export function normalizeInvoiceErrors(error: Error): InvoiceValidationErrors {
  const normalized: InvoiceValidationErrors = { lines: {} }
  if (!(error instanceof ApiError) || !error.errors) return normalized
  for (const [key, messages] of Object.entries(error.errors)) {
    const message = messages.join(' ')
    const match = /^lines\.(\d+)\.(.+)$/.exec(key)
    if (match) {
      const index = Number(match[1])
      const field = match[2]
      if (field === undefined) continue
      normalized.lines[index] = { ...normalized.lines[index], [field]: message }
    } else {
      normalized.header = { ...normalized.header, [key]: message }
    }
  }
  return normalized
}

export const DEFAULT_PRESETS: PaymentTerm[] = [
  {
    id: 'DUE',
    code: 'DUE',
    name: { en: 'Due on receipt', ar: 'مستحق عند الاستلام' },
    net_days: 0,
    day_of_month: null,
    discount_percent: null,
    discount_days: null,
    is_active: true,
  },
  {
    id: 'NET-15',
    code: 'NET-15',
    name: { en: 'Net 15', ar: 'صافي 15' },
    net_days: 15,
    day_of_month: null,
    discount_percent: null,
    discount_days: null,
    is_active: true,
  },
  {
    id: 'NET-30',
    code: 'NET-30',
    name: { en: 'Net 30', ar: 'صافي 30' },
    net_days: 30,
    day_of_month: null,
    discount_percent: null,
    discount_days: null,
    is_active: true,
  },
  {
    id: 'NET-45',
    code: 'NET-45',
    name: { en: 'Net 45', ar: 'صافي 45' },
    net_days: 45,
    day_of_month: null,
    discount_percent: null,
    discount_days: null,
    is_active: true,
  },
  {
    id: 'NET-60',
    code: 'NET-60',
    name: { en: 'Net 60', ar: 'صافي 60' },
    net_days: 60,
    day_of_month: null,
    discount_percent: null,
    discount_days: null,
    is_active: true,
  },
]

export function deriveDueDate(invoiceDate: string, days: number): string {
  if (!invoiceDate || !Number.isFinite(days) || days < 0) return ''
  const parts = invoiceDate.split('-').map((part) => Number.parseInt(part, 10))
  if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) return ''
  const [year, month, day] = parts
  const date = new Date(Date.UTC(year, month - 1, day + days))
  if (Number.isNaN(date.getTime())) return ''
  return localIsoDate(date)
}

export function customerPaymentTermValues(
  availableTerms: readonly PaymentTerm[],
  paymentTermId: string | null | undefined,
  legacyTerms: string | null | undefined,
  invoiceDate: string,
): { payment_terms_id: string; payment_terms_days: string; due_date: string } | undefined {
  const term =
    (paymentTermId ? availableTerms.find((entry) => entry.id === paymentTermId) : undefined) ??
    (legacyTerms ? matchPaymentTerm(availableTerms, legacyTerms) : undefined)
  if (!term || term.net_days === null) return undefined

  return {
    payment_terms_id: term.id,
    payment_terms_days: String(term.net_days),
    due_date: deriveDueDate(invoiceDate, term.net_days),
  }
}

export function invoiceFormPaymentTermState({
  values,
  record,
  defaultTermsDays,
  availableTerms,
  invoiceDate,
  companyDefaultTerm,
  isEditing,
  isDuplicating,
  customTerms,
  dueDateEditing,
}: {
  values: Record<string, string>
  record?: Pick<Invoice, 'payment_terms_days' | 'payment_terms_id' | 'due_date'> | null
  defaultTermsDays: number
  availableTerms: readonly PaymentTerm[]
  invoiceDate: string
  companyDefaultTerm?: PaymentTerm
  isEditing: boolean
  isDuplicating: boolean
  customTerms: boolean
  dueDateEditing: boolean
}) {
  const termsString =
    values.payment_terms_days ?? String(record?.payment_terms_days ?? defaultTermsDays)
  const effectiveTerms = Number.parseInt(termsString, 10)
  const validTerms =
    Number.isFinite(effectiveTerms) && effectiveTerms >= 0 ? effectiveTerms : defaultTermsDays
  const rawDueDate = isDuplicating ? '' : (values.due_date ?? record?.due_date ?? '')
  const calculatedDefaultDueDate = deriveDueDate(invoiceDate, validTerms)
  const dueDate = rawDueDate || calculatedDefaultDueDate
  const paymentTermId = values.payment_terms_id ?? record?.payment_terms_id ?? ''
  const matchedTerm =
    availableTerms.find((term) => paymentTermId && term.id === paymentTermId) ??
    (!isEditing && !isDuplicating ? companyDefaultTerm : undefined) ??
    availableTerms.find((term) => term.net_days === validTerms)

  return {
    termsString,
    validTerms,
    dueDate,
    matchedTerm,
    selectedPresetValue: customTerms ? 'custom' : matchedTerm ? matchedTerm.id : 'custom',
    dueDateIsEditable:
      dueDateEditing || Boolean(rawDueDate && rawDueDate !== calculatedDefaultDueDate),
  }
}

export function deriveDaysBetween(baseDate: string, targetDate: string): number | null {
  if (!baseDate || !targetDate) return null
  const baseParts = baseDate.split('-').map((part) => Number.parseInt(part, 10))
  const targetParts = targetDate.split('-').map((part) => Number.parseInt(part, 10))
  if (
    baseParts.length !== 3 ||
    targetParts.length !== 3 ||
    !baseParts[0] ||
    !baseParts[1] ||
    !baseParts[2] ||
    !targetParts[0] ||
    !targetParts[1] ||
    !targetParts[2]
  ) {
    return null
  }
  const baseUtc = Date.UTC(baseParts[0], baseParts[1] - 1, baseParts[2])
  const targetUtc = Date.UTC(targetParts[0], targetParts[1] - 1, targetParts[2])
  const diffDays = Math.round((targetUtc - baseUtc) / (1000 * 60 * 60 * 24))
  return diffDays >= 0 ? diffDays : null
}

export function subtractPreview(total: string, discount: string): string {
  return subtractDecimalStrings(total, discount)
}
